import React from "react";
import { useIntl } from "react-intl";
import { Map, Marker, ZoomControl } from "pigeon-maps";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";

import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import EmailIcon from "@mui/icons-material/Email";
import LocalPhoneIcon from "@mui/icons-material/LocalPhone";

import { TLocation } from "types";

import { Tupel } from "components";

import { TCoordinate, calcCrow } from "./helper";

interface ILocation {
  paperProps?: any;
  location: TLocation;
  hideName?: boolean;
  origin?: TCoordinate;
}

const Location: React.FC<ILocation> = ({
  paperProps = { sx: { p: 2 } },
  location,
  hideName = false,
  origin,
}): JSX.Element | null => {
  const { formatMessage: t } = useIntl();
  const [coordinates, setCoordinates] = React.useState<TCoordinate>();
  const [distance, setDistance] = React.useState<number>(0);

  React.useEffect(() => {
    if (!coordinates) return;
    if (!origin) return;
    setDistance(calcCrow({ from: origin, to: coordinates }));
  }, [origin, coordinates]);

  React.useEffect(() => {
    const loadCoordinates = async (payload: any) => {
      const url = "https://service.carbando.eu/locate";
      const options = { method: "POST", body: JSON.stringify(payload) };
      const response = await fetch(url, options);
      const data = await response.json();
      if (data.lat && data.lon) setCoordinates(data);
    };

    if (!location) return;

    const { id, lat = null, lon = null, zip } = location;
    if (lat && lon) {
      setCoordinates({ lat, lon });
    } else if (zip) {
      const _lat = parseFloat(
        t({
          id: `location.${id}.lat`,
          defaultMessage: "",
        })
      );
      const _lon = parseFloat(
        t({
          id: `location.${id}.lon`,
          defaultMessage: "",
        })
      );
      if (!(_lat && _lon)) {
        loadCoordinates(location);
      } else {
        setCoordinates({ lat: _lat, lon: _lon });
      }
    }
  }, [location, t]);

  if (!location) return null;

  const { id, name: defaultName, address, zip, city } = location;

  const name = t({
    id: `location.${id}.name`,
    defaultMessage: defaultName,
  });

  const whatsapp = t({
    id: `location.${id}.whatsapp`,
    defaultMessage: "none",
  });
  const email = t({
    id: `location.${id}.email`,
    defaultMessage: "none",
  });
  const phone = t({
    id: `location.${id}.phone`,
    defaultMessage: "none",
  });

  return (
    <>
      {!hideName && <Tupel label="Standort" text={name} />}
      <Tupel label="Straße" text={address} />
      <Tupel label="Ort" text={`${zip} ${city}`} />
      {!!distance && (
        <Tupel label="Entfernung" number={distance} unit="km" digits={0} />
      )}
      <Stack
        direction="row"
        spacing={2}
        sx={{ mt: 2, justifyContent: "center" }}
      >
        {email !== "none" && (
          <Button
            variant="contained"
            color="primary"
            size="small"
            href={"mailto:" + email + "?subject=Interesse"}
            target="_blank"
            startIcon={<EmailIcon />}
            aria-label="Whatsapp"
          >
            E-Mail
          </Button>
        )}
        {phone !== "none" && (
          <Button
            variant="contained"
            color="primary"
            size="small"
            href={"tel:" + phone}
            target="_blank"
            startIcon={<LocalPhoneIcon />}
            aria-label="Whatsapp"
          >
            Telefon
          </Button>
        )}
        {whatsapp !== "none" && (
          <Button
            variant="contained"
            color="primary"
            size="small"
            href={whatsapp}
            target="_blank"
            startIcon={<WhatsAppIcon />}
            aria-label="Whatsapp"
          >
            Whatsapp
          </Button>
        )}
      </Stack>

      {coordinates && (
        <Box
          sx={{
            height: { xs: "300px", md: "500px" },
            border: "solid 1px",
            p: 1,
            mt: 3,
          }}
        >
          <Map center={[coordinates.lat, coordinates.lon]} zoom={12}>
            <ZoomControl />
            <Marker
              width={30}
              color="green"
              anchor={[coordinates.lat, coordinates.lon]}
            />
          </Map>
        </Box>
      )}
    </>
  );
};

export default Location;
